import React, { useState, useEffect } from 'react';
import { Storage } from 'aws-amplify';
import {
  Row,
  Col,
  Card,
  Input,
  Tooltip,
  Popconfirm,
  Modal,
  Form,
  Typography,
  message,
} from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { multiImageZipKey } from "../../../../core/utils/dataloading/multiImageZipKey";
import { getImageType } from "../../../../core/utils/general/strings";
import { validDimensions } from "../../../../core/utils/constants/constants";
import BulkUploadResultsModal from "./creatives/BulkUploadResultModal";
import MediaUploadModal from "../../Creatives/components/MediaUploadModal";
import { useLibraryCreativesContext } from "../../../../core/components/creatives/library/useLibraryCreativesContext";
import "./../../Creatives/library/creativestyle.css";
import Button from "../../../shared/globalStyling/Button";
import SearchBar from "../../Creatives/library/SearchBar";
import AddCreativesModal from "../../Creatives/components/AddCreativesModal";
import { uploadToS3Storage } from "../../Creatives/utils/mediaUtils";

const CreativeTab = ({
  onChangeStateValue,
  creativeName,
  onDeleteMedias,
  onCreateAdgroup,
  ids,
  setIds,
  setRenewedCreatives,
  creativeKeys,
  bulkCreateOrgMedia,
  searchText,
  handleSearch,
  clearAllSelections,
}) => {
  const { refetchMedias, createOrgMedia, orgMedias, adGroupCreatives, updateAdGroupCreatives } =
    useLibraryCreativesContext() || {};

  /* eslint-disable */
  useEffect(() => {
    const getNewOrgMedias =
      orgMedias &&
      orgMedias
        .filter(val => val.type && val.type.toUpperCase() !== "VIDEO") // Filter out videos
        .map(val => {
          const urlOrgMedia = Storage.get(val.key, {
            expires: 600000,
          });

          urlOrgMedia.then(response => {
            return (val.url = response);
          });
          return val;
        });

    setRenewedCreatives(getNewOrgMedias);
  }, [orgMedias]);
  /* eslint-enable */

  const [
    showUploadResultsTableModal,
    setShowUploadResultsTableModal,
  ] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);

  const [adGroupModal, setAdGroupModal] = useState(false);
  const [adGroupModalVisible, setAdGroupModalVisible] = useState(false);
  const [selectedAdGroupIds, setSelectedAdGroupIds] = useState([]);
  const [selectedAdUnits, setSelectedAdUnits] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const [formAdGroup] = Form.useForm();
  const [uploadResult, setUploadResults] = useState([]);

  const { Title } = Typography;

  // Clear upload results when upload modal is opened
  useEffect(() => {
    if (uploadModal) {
      setUploadResults([]);
    }
  }, [uploadModal]);

  // Handle uploads from MediaUploadModal
  const handleMediaUpload = async (mediaData, mode) => {
    if (mode === "single") {
      // Handle single upload
      setUploadLoading(true);
      try {
        const media = mediaData[0];
        await createOrgMedia({
          variables: {
            mediatype: "STILL",
            key: media.key,
            url: media.url,
            width: media.width,
            height: media.height,
            name: media.name,
            size: media.size,
            fileType: media.fileType,
          },
        });

        // Refetch data before showing success and closing modal
        await refetchMedias();

        // Only show success message after data is actually saved and refetched
        message.success('Image Creative saved successfully!');
        setUploadModal(false);
      } catch (error) {
        message.error(`File upload failed: ${error.message}`);
      } finally {
        setUploadLoading(false);
      }
    } else if (mode === "bulk") {
      // Handle bulk upload
      setUploadLoading(true);
      setUploadComplete(false);
      setUploadResults([]);
      setUploadModal(false);
      setShowUploadResultsTableModal(true);

      const results = [];

      try {
        for (const mediaFile of mediaData) {
          const { file, dimensions, name } = mediaFile;

          try {
            // Upload to S3
            const response = await uploadToS3Storage(file, file.type);

            // Prepare result for UI
            const result = {
              name,
              dimensions: `${dimensions.width}x${dimensions.height}`,
              fileSize: file.size,
              status: true,
            };

            setUploadResults(prev => [...prev, result]);

            // Prepare for bulk create
            results.push({
              type: "STILL",
              key: response.key,
              url: response.url,
              width: dimensions.width,
              height: dimensions.height,
              name,
              size: file.size,
              fileType: file.type.split("/")[1].toUpperCase(),
            });
          } catch (uploadError) {
            console.error(`Failed to upload ${file.name}:`, uploadError);

            const failedResult = {
              name,
              dimensions: `${dimensions.width}x${dimensions.height}`,
              fileSize: file.size,
              status: false,
              error: "Upload failed",
            };

            setUploadResults(prev => [...prev, failedResult]);
          }
        }

        // Bulk create all images
        await bulkCreateOrgMedia(results, [], true);
        await refetchMedias();

        setUploadComplete(true);
        message.success(`Successfully uploaded ${results.length} image(s)`);

        // Close modal immediately after success
        setShowUploadResultsTableModal(false);
      } catch (error) {
        message.error("Bulk upload failed");
      } finally {
        setUploadLoading(false);
      }
    }
  };

  const handleCreateAdGroup = async () => {
    try {
      const values = await formAdGroup.validateFields();
      const adGroupName = values.name;

      const remakeIds = ids.map(idVal => {
        const getId = idVal;
        return { id: getId };
      });

      // Pass the data directly to avoid state timing issues
      await onCreateAdgroup(adGroupName, remakeIds);

      // Clean up modal state after successful creation
      // Note: selection clearing is handled by parent LibraryCreativesViewImproved
      formAdGroup.resetFields();
      setAdGroupModal(false);
      setAdGroupModalVisible(false);
    } catch (error) {
      message.error('Failed to create ad group');
    }
  };

  const handleDeleteMedias = async () => {
    try {
      await onDeleteMedias(ids);
      await setRenewedCreatives(orgMedias.filter((item) => !ids.includes(item.id)));
      await setIds([]);
    } catch (err) {
      message.error(`Error deleting media creative(s): ${err}`);
    }
  };

  // Handle assigning display creatives to ad groups
  const handleAssignAdUnitsInternal = async (selectedAdGroupIds, selectedIds) => {
    setAssigning(true);
    try {
      // Use the proper assignment approach - connect media to existing ad groups
      await Promise.all(
        selectedAdGroupIds.map(adGroupId =>
          updateAdGroupCreatives({
            variables: {
              id: adGroupId,
              data: {
                medias: {
                  connect: selectedIds.map(id => ({ id })),
                },
              },
            },
          })
        )
      );
      message.success("Display creatives assigned to ad group(s)!");
      setAdGroupModalVisible(false);
      setSelectedAdGroupIds([]);
      setSelectedAdUnits([]);

      // Clear local selections immediately
      setIds([]);

      // Clear all selections through parent state management
      if (clearAllSelections) {
        clearAllSelections();
      }

      await refetchMedias();
    } catch (error) {
      message.error("Failed to assign display creatives to ad group(s)");
    } finally {
      setAssigning(false);
    }
  };

  const handleDownloadAdUnits = async () => {
    if (!creativeKeys || creativeKeys.length === 0) {
      return message.warning("Please select ad units to download");
    }

    setDownloadLoading(true);
    try {
      const results = await multiImageZipKey(
        `Creatives (${creativeKeys.length} images)`,
        creativeKeys
      );

      // Check for complete failure
      if (results.success === false) {
        const failedCount = Array.isArray(results.failedDownloads)
          ? results.failedDownloads.length
          : results.failedDownloads;
        message.error(
          `Failed to download ${failedCount} file(s). Files may be deleted or inaccessible.`
        );
      }
      // Check for partial failure (some succeeded, some failed)
      else if (
        (Array.isArray(results.failedDownloads) && results.failedDownloads.length > 0) ||
        (typeof results.failedDownloads === 'number' && results.failedDownloads > 0) ||
        results.failedKeys?.length > 0
      ) {
        const successCount = results.successfulDownloads || 0;
        const failedCount = Array.isArray(results.failedDownloads)
          ? results.failedDownloads.length
          : results.failedDownloads || results.failedKeys?.length || 0;
        message.warning(
          `Partial download: ${successCount} creative(s) downloaded successfully, ${failedCount} failed. Failed files may be deleted or inaccessible.`
        );
      }
      // Complete success
      else {
        const successCount = results.successfulDownloads || creativeKeys.length;
        message.success(
          `Successfully downloaded ${successCount} creative(s)`
        );
      }
    } catch (error) {
      message.error("Failed to download creatives");
    } finally {
      setDownloadLoading(false);
    }
  };
  return (
    <>
      <BulkUploadResultsModal
        visible={showUploadResultsTableModal}
        uploadResults={uploadResult}
        uploadInProgress={uploadLoading}
        uploadComplete={uploadComplete}
        onCancel={() => {
          setShowUploadResultsTableModal(false);
        }}
      />

      {/* Shared Media Upload Modal */}
      <MediaUploadModal
        visible={uploadModal}
        onCancel={() => setUploadModal(false)}
        onUpload={handleMediaUpload}
        mediaType="IMAGE"
        title="Upload Image(s)"
        validDimensions={validDimensions}
        loading={uploadLoading}
      />
      <AddCreativesModal
        setAdGroupModalVisible={setAdGroupModalVisible}
        adGroupModalVisible={adGroupModalVisible}
        setAdGroupModal={setAdGroupModal}
        ids={ids} // selected ad units (just ids)
        orgMedias={orgMedias} // for table info
        selectedAdUnits={selectedAdUnits} // selected adunits (full info)
        adGroupCreatives={adGroupCreatives} // available adgroups
        selectedAdGroupIds={selectedAdGroupIds}
        setSelectedAdGroupIds={setSelectedAdGroupIds}
        handleAssignAdUnits={handleAssignAdUnitsInternal}
        assigning={assigning}
      />
      <Modal
        title={"NEW DISPLAY AD GROUP"}
        open={adGroupModal}
        centered
        footer={null}
        closable={false}
      >
        <Form layout="vertical" onFinish={handleCreateAdGroup} form={formAdGroup}>
          <Row type="flex" justify="space-around">
            <Col span={24}>
              <Form.Item
                label="Ad Group Name"
                name="name"
                rules={[{ required: true }]}
              >
                <Input
                  placeholder="Name of Ad Group"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row type="flex" justify="space-around" align="middle">
            <Col span={24}>
              <div style={{ display: "flex", justifyContent: "end" }}>
                <Button
                  htmlType="button"
                  type="default"
                  onClick={() => {
                    setAdGroupModal(false);
                    formAdGroup.resetFields();
                  }}
                >
                  Back
                </Button>
                <Button
                  htmlType="submit"
                  type="primary"
                >
                  Create
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Row>
        <Col xs={24} xl={24}>
          <Card bodyStyle={{ padding: 10 }}>
            <Row align="middle" className="ant-row-justify-space-between">
              <Col>
                <Title level={4}>Display Ad Units</Title>
              </Col>
              <Col>
                <SearchBar searchText={searchText} onSearch={handleSearch} />
              </Col>
            </Row>
            <Row align="middle" className="ant-row-justify-space-between">
              <Col xs={24} sm={16} md={16} lg={14}>
                <Row>
                  <Popconfirm
                    placement="topLeft"
                    title={`Are you sure you want to delete the selected creatives? This action cannot be undone.`}
                    onConfirm={() => handleDeleteMedias()}
                    okText="Delete"
                    cancelText="Cancel"
                  >
                    <Tooltip placement="bottom" title={`Delete selected creatives`}>
                      <Button
                        type="default"
                        disabled={!ids || ids.length === 0}
                      >
                        Delete
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                  <Button
                    type="default"
                    disabled={!ids || ids.length === 0 || downloadLoading}
                    loading={downloadLoading}
                    onClick={handleDownloadAdUnits}
                  >
                    Download Ad Unit
                  </Button>
                  <Button
                    type="default"
                    disabled={!ids || ids.length === 0}
                    onClick={() => {
                      setSelectedAdUnits(
                        orgMedias.filter(item => ids.includes(item.id))
                      );
                      setAdGroupModalVisible(true);
                    }}
                  >
                    Assign to Ad Groups
                  </Button>
                </Row>
              </Col>
              <Col xs={24} sm={8} md={8} lg={10} style={{ textAlign: "right" }}>
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => setUploadModal(true)}
                  disabled={false}
                >
                  Upload Display Creative(s)
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default CreativeTab;
